/**
 * Completes the OIDC redirect before the application starts.
 *
 * This has to happen outside the component tree. The callback used to be a
 * route, and the exchange ran in its `created()` hook — but the app dispatches
 * an FSM message at boot which navigates, and that unmounted the callback page
 * mid-exchange. The promise was abandoned, no tokens were stored, the guard saw
 * an unauthenticated user and redirected to sign in, which produced another
 * code, and round it went.
 *
 * Doing it here removes the race rather than papering over it: nothing is
 * mounted, nothing can navigate, and by the time the router exists the session
 * either is or is not established.
 *
 * Returns the path the user was originally heading for, or null when this was
 * not a callback load.
 */

import oidc from './oidcClient';

const CALLBACK_PATH = '/auth/callback';

/**
 * The in-flight exchange, so the navigation guard can wait for it instead of
 * deciding the user is unauthenticated while tokens are still being fetched.
 */
let pending = null;

export function pendingSession() {
  return pending;
}

export function isCallbackUrl() {
  // Hash history: the router's path lives after the `#`, and the static bridge
  // page under public/ forwards the real redirect there.
  return (window.location.hash || '').startsWith(`#${CALLBACK_PATH}`)
    || window.location.pathname === CALLBACK_PATH;
}

export function bootstrapSession() {
  if (!isCallbackUrl()) {
    return null;
  }

  pending = exchange().finally(() => { pending = null; });

  return pending;
}

async function exchange() {
  const hashQuery = (window.location.hash.split('?')[1]) || '';
  const search = hashQuery ? `?${hashQuery}` : window.location.search;

  try {
    const returnTo = await oidc.completeLogin(search);

    // Drop the authorization code from the address bar so a back button cannot
    // replay it, and so the router starts on a clean route.
    window.history.replaceState({}, document.title, `${window.location.pathname}#${returnTo || '/welcome'}`);

    return returnTo || '/welcome';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[auth] sign-in could not be completed:', error);
    window.history.replaceState({}, document.title, `${window.location.pathname}#/sign-in-problem`);
    return '/sign-in-problem';
  }
}

export default bootstrapSession;
